import React, { useState, useMemo } from 'react';
import { Search, LayoutGrid, List as ListIcon, X, Mail, MoreHorizontal, Users, User } from 'lucide-react';
import { TextField, InputAdornment, IconButton, Skeleton } from '@mui/material';
import UserAvatar from '../components/UserAvatar';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

const Row = ({ index, style, data }) => {
  const { filteredTeam, columnCount, gap, padding, isLoading, isMobile } = data;
  const startIndex = index * columnCount;
  
  if (isLoading) {
    return (
      <div style={{ ...style, padding: `0 ${padding}px`, boxSizing: 'border-box', display: 'flex', gap: `${gap}px` }}>
        {Array.from({ length: columnCount }).map((_, i) => (
          <div key={`skeleton-${i}`} style={{ flex: 1, minWidth: 0, paddingBottom: `${gap}px`, height: '100%', boxSizing: 'border-box' }}>
            <MemberCardSkeleton isMobile={isMobile} />
          </div>
        ))}
      </div>
    );
  }

  const rowMembers = filteredTeam.slice(startIndex, startIndex + columnCount);
  
  return (
    <div style={{ ...style, padding: `0 ${padding}px`, boxSizing: 'border-box', display: 'flex', gap: `${gap}px` }}>
      {rowMembers.map((member, i) => (
        <div key={i} style={{ flex: 1, minWidth: 0, paddingBottom: `${gap}px`, height: '100%', boxSizing: 'border-box' }}>
          <MemberCard member={member} isMobile={isMobile} />
        </div>
      ))}
      {Array.from({ length: columnCount - rowMembers.length }).map((_, i) => (
        <div key={`empty-${i}`} style={{ flex: 1, minWidth: 0 }} />
      ))}
    </div>
  );
};

const MemberCardSkeleton = ({ isMobile }) => {
  if (isMobile) {
    return (
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center h-full w-full">
        <div className="shrink-0 mr-4">
          <Skeleton variant="circular" width={48} height={48} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="40%" height={16} />
        </div>
        <div className="shrink-0 flex items-center ml-2">
          <Skeleton variant="circular" width={32} height={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl pt-6 text-center shadow-sm border border-slate-100 flex flex-col items-center h-full w-full">
      <div className="relative mb-4">
        <Skeleton variant="circular" width={72} height={72} />
      </div>
      <div className="px-5 w-full flex-1 flex flex-col justify-start pb-4">
        <Skeleton variant="text" width="70%" height={24} sx={{ mx: 'auto', mb: 0.5 }} />
        <Skeleton variant="text" width="50%" height={16} sx={{ mx: 'auto' }} />
      </div>
      <div className="w-full flex items-center justify-center mt-auto pt-3 pb-3 border-t border-slate-100">
        <div className="flex-1 flex items-center justify-center p-2"><Skeleton variant="rounded" width={20} height={20} /></div>
      </div>
    </div>
  );
};

const MemberCard = ({ member, isMobile }) => {
  const name = member.EMP_NAME || member.name;
  const designation = member.DESIGNATION || member.designation || "Team Member";
  const email = member.EMP_WORK_EMAIL || member.email;
  
  if (isMobile) {
    return (
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center h-full w-full hover:shadow-md hover:border-slate-200 transition-all duration-300">
        <div className="shrink-0 mr-4">
          <UserAvatar 
            name={name} 
            imageUrl={member.img_dir && member.img_dir !== 'null' && member.img_dir !== 'undefined' ? `${process.env.REACT_APP_PUBLIC_URL}/${member.img_dir}` : null} 
            size={48} 
            className="shadow-sm m-0" 
          />
        </div>
        
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="text-[15px] font-bold text-slate-900 leading-snug truncate" title={name}>
            {name}
          </div>
          <div className="text-[13px] font-medium text-slate-500 truncate" title={designation}>
            {designation}
          </div>
        </div>
        
        <div className="shrink-0 flex items-center ml-2">
          <a href={email ? `mailto:${email}` : '#'} className="p-2 hover:bg-slate-50 transition-colors rounded-full text-slate-400 hover:text-slate-700" title={email || "Email"}>
            <Mail className="w-[18px] h-[18px]" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-3xl pt-6 text-center shadow-sm border border-slate-100 hover:shadow-md hover:border-slate-200 transition-all duration-300 flex flex-col items-center h-full w-full"
    >
      <div className="relative mb-4">
        <UserAvatar 
          name={name} 
          imageUrl={member.img_dir && member.img_dir !== 'null' && member.img_dir !== 'undefined' ? `${process.env.REACT_APP_PUBLIC_URL}/${member.img_dir}` : null} 
          size={72} 
          className="shadow-sm m-0" 
        />
      </div>

      <div className="w-full flex flex-col flex-1 items-center min-w-0 px-6">
        <div className="flex flex-col items-center min-w-0 w-full">
          <div className="text-[15px] font-bold text-slate-900 leading-snug mb-1 truncate max-w-full" title={name}>
            {name}
          </div>
          <div className="text-[12px] font-medium text-slate-500 truncate max-w-full" title={designation}>
            {designation}
          </div>
        </div>
      </div>
        
      <div className="w-full flex items-center justify-center mt-auto pt-3 pb-3 border-t border-slate-100">
        <a href={email ? `mailto:${email}` : '#'} className="flex-1 flex items-center justify-center p-2 hover:bg-slate-50 transition-colors rounded-bl-3xl" title={email || "Email"}>
          <Mail className="w-[18px] h-[18px] text-slate-400 hover:text-slate-700" />
        </a>
      </div>
    </div>
  );
};

const TeamDirectory = ({ team, isLoading }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [alphaFilter, setAlphaFilter] = useState('All');
  
  const alphabets = ['All', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];

  const filteredTeam = useMemo(() => {
    if (!team) return [];
    return team.filter((member) => {
      const query = searchQuery.toLowerCase();
      const name = (member.EMP_NAME || member.name || '');
      const role = (member.DESIGNATION || member.designation || 'Team Member').toLowerCase();
      
      const matchesSearch = name.toLowerCase().includes(query) || role.includes(query);
      const matchesAlpha = alphaFilter === 'All' || name.toUpperCase().startsWith(alphaFilter);
      
      return matchesSearch && matchesAlpha;
    });
  }, [team, searchQuery, alphaFilter]);

  const totalItems = isLoading ? 12 : filteredTeam.length;

  return (
    <div className="bg-transparent flex flex-col w-full flex-1 min-h-0">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-2 p-4 flex flex-col gap-1 shrink-0 mb-1">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-1 pb-3 border-b border-slate-200">
          <div className="flex flex-col">
            <div className="flex items-center gap-3 mb-1.5">
              <h2 className="text-[20px] font-bold text-slate-900 m-0 leading-none">Team Members</h2>
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-600 font-bold text-[12px] rounded-full leading-none border border-blue-100 shadow-sm">
                 <Users className="w-3.5 h-3.5" />
                 <span>{totalItems} Members</span>
              </div>
            </div>
            <p className="text-[13px] text-slate-500 font-medium m-0">Meet the people behind our organization.</p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-full sm:w-[320px]">
              <TextField
                size="small"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="w-4 h-4 text-slate-400" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                        <X className="w-4 h-4 text-slate-400" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: {
                    height: '44px',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(15,23,42,0.02)',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused fieldset': { borderColor: '#2563EB', borderWidth: '1px' },
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Alphabet Filter Row */}
        <div className="flex items-center gap-0.5 overflow-x-auto custom-scrollbar pb-1 pt-2 mb-1">
          {alphabets.map(letter => (
            <button
              key={letter}
              onClick={() => setAlphaFilter(letter)}
              className={`min-w-[24px] h-[24px] px-1 flex items-center justify-center rounded-full text-[11px] font-bold transition-colors shrink-0 ${
                alphaFilter === letter 
                  ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20' 
                  : 'text-slate-500 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-transparent flex-1 relative min-h-0 w-full">
        {totalItems > 0 ? (
          <AutoSizer>
            {({ height, width }) => {
              if (height === undefined || width === undefined) return null;
              const padding = 8; 
              const gap = 20; 
              const actualWidth = width - (padding * 2);

              const minColumnWidth = 240;
              let columnCount = Math.floor((actualWidth + gap) / (minColumnWidth + gap));
              columnCount = Math.max(1, columnCount); 
              
              if (width < 640) columnCount = 1; 
              else if (width < 768) columnCount = 2;
              else if (width < 1024) columnCount = 3;
              else if (width < 1280) columnCount = 4;
              else columnCount = Math.max(columnCount, 5); 
              
              const isMobile = columnCount === 1;
              const rowCount = Math.ceil(totalItems / columnCount);
              const rowGap = isMobile ? 12 : 20;
              const itemHeight = isMobile ? 94 : 240; // card height + gap

              return (
                <div style={{ height: '100%', paddingTop: `${padding}px`, boxSizing: 'border-box' }}>
                  <List
                    height={height - padding}
                    itemCount={rowCount}
                    itemSize={itemHeight}
                    width={width}
                    className="custom-scrollbar"
                    itemData={{ filteredTeam, columnCount, gap: rowGap, padding, isLoading, isMobile }}
                  >
                    {Row}
                  </List>
                </div>
              );
            }}
          </AutoSizer>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-full text-slate-500 py-12">
            <div className="relative w-36 h-36 mb-2">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 170C138.66 170 170 138.66 170 100C170 61.3401 138.66 30 100 30C61.3401 30 30 61.3401 30 100C30 138.66 61.3401 170 100 170Z" fill="#F3E8FF"/>
                <path d="M115 115L145 145" stroke="#A855F7" strokeWidth="12" strokeLinecap="round"/>
                <circle cx="95" cy="95" r="35" fill="none" stroke="#A855F7" strokeWidth="12"/>
                <path d="M85 95C85 89.4772 89.4772 85 95 85C100.523 85 105 89.4772 105 95" stroke="#A855F7" strokeWidth="6" strokeLinecap="round"/>
                <circle cx="95" cy="105" r="4" fill="#A855F7"/>
                <path d="M95 102 C85 102, 75 110, 75 115 L115 115 C115 110, 105 102, 95 102 Z" fill="#A855F7"/>
                <circle cx="95" cy="88" r="8" fill="#A855F7"/>
                <path d="M75 105 C68 105, 60 112, 60 115 L70 115 C70 112, 72 108, 75 108 Z" fill="#A855F7"/>
                <circle cx="72" cy="92" r="6" fill="#A855F7"/>
                <path d="M115 105 C122 105, 130 112, 130 115 L120 115 C120 112, 118 108, 115 108 Z" fill="#A855F7"/>
                <circle cx="118" cy="92" r="6" fill="#A855F7"/>
                <path d="M30 60 Q50 40 70 50" stroke="#E9D5FF" strokeWidth="4" strokeLinecap="round" fill="none"/>
                <circle cx="160" cy="50" r="8" fill="#E9D5FF"/>
                <circle cx="40" cy="150" r="6" fill="#E9D5FF"/>
                <circle cx="150" cy="160" r="5" fill="#E9D5FF"/>
                <path d="M150 140 Q170 130 180 150" stroke="#E9D5FF" strokeWidth="4" strokeLinecap="round" fill="none"/>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-800">No team members found</h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(TeamDirectory);

